import React from 'react';
import PropTypes from 'prop-types';
import { useLazyQuery } from '@apollo/client';

import { useToast } from 'core/components/toast';
import { GET_PAYMENT_RECEIPT } from '../../payment.queries';
import useStyles from './receipt-link.styles';

// A transaction gets a receipt when it represents a received payment: paid, or a charge that was
// (partially) refunded (which still displays as "Paid"). Refund rows themselves don't (#34203).
// `isPaid` is the authoritative paid flag from Stripe/ChargeDesk — key on it so paid mortgagee/escrow
// charges (whose ChargeDesk status string isn't always the literal 'paid') still get a receipt, while
// keeping the status allowlist as a fallback for payloads served before `isPaid` existed. (#35215)
// An in-flight ACH payment ('pending' via Stripe's Charges API, 'processing' via PaymentIntents) also
// gets a receipt — the server renders it with "Pending" wording instead of "paid" (#35241).
const RECEIPT_ELIGIBLE_STATUSES = ['paid', 'partially refunded', 'pending', 'processing'];

export const isReceiptEligible = (transaction) =>
  !(transaction.isRefund ?? transaction.id?.startsWith('re_')) &&
  (transaction.isPaid === true || RECEIPT_ELIGIBLE_STATUSES.includes(transaction.paymentStatus));

/**
 * A "Receipt" link for a past payment. On click it renders the branded receipt PDF server-side
 * (getPaymentReceipt) and opens the presigned URL. Only the policy + transaction id are sent — the
 * server looks the transaction up and derives the receipt's amount/date/method from the real record.
 */
const ReceiptLink = ({ transaction, policyId }) => {
  const classes = useStyles();
  const toast = useToast();
  const [fetchReceipt, { loading }] = useLazyQuery(GET_PAYMENT_RECEIPT, { fetchPolicy: 'no-cache' });

  const handleClick = async () => {
    try {
      const { data } = await fetchReceipt({
        variables: {
          input: {
            policyId,
            transactionId: transaction.id
          }
        }
      });
      const url = data?.getPaymentReceipt;
      if (url) {
        window.open(url, '_blank', 'noopener,noreferrer');
      } else {
        toast.notify({ type: 'error', message: 'Could not generate the receipt.' });
      }
    } catch (error) {
      toast.notify({ type: 'error', message: error.message || 'Could not generate the receipt.' });
    }
  };

  return (
    <button type="button" className={classes.link} onClick={handleClick} disabled={loading}>
      {loading ? 'Opening…' : 'Receipt'}
    </button>
  );
};

ReceiptLink.propTypes = {
  transaction: PropTypes.object.isRequired,
  policyId: PropTypes.string.isRequired
};

export default ReceiptLink;
